const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i;

function cleanText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function objectValue(value, keys) {
  if (!value || typeof value !== "object") return "";
  for (const key of keys) {
    const candidate = cleanText(value?.[key]);
    if (candidate) return candidate;
  }
  return "";
}

export function paymentCardLast4(row = {}) {
  const meta = row?.meta && typeof row.meta === "object" ? row.meta : {};
  const candidate = [
    objectValue(row, ["card_last4", "last4"]),
    objectValue(meta, ["card_last4", "last4"]),
    objectValue(meta?.card, ["last4"]),
    objectValue(meta?.authorization, ["last4"]),
    objectValue(meta?.payment_method, ["last4"]),
  ].find((value) => /^\d{4}$/.test(value));
  return candidate || "";
}

export function paymentMethodLabel(row = {}, t = (_, fallback) => fallback) {
  const meta = row?.meta && typeof row.meta === "object" ? row.meta : {};
  const provider = cleanText(meta?.provider || row?.provider).toLowerCase();
  const method = cleanText(row?.method || meta?.method || provider).toLowerCase();
  const last4 = paymentCardLast4(row);

  if (["paystack", "card", "credit_card", "debit_card"].includes(method) || provider === "paystack") {
    return last4
      ? t("payment.methods.cardEnding", "Card ending **** {last4}", { last4 })
      : t("payment.methods.card", "Card");
  }
  if (["mtn_momo", "mtn-momo", "mtn_mtn_momo"].includes(method) || provider === "mtn_momo") return "MTN MoMo";
  if (["instant_eft", "eft"].includes(method) || provider === "instant_eft") return "Instant EFT";
  if (["qr", "scan_to_pay", "scan-to-pay"].includes(method) || provider === "scan_to_pay") return "Scan to Pay";
  if (["sponsor-payment-link", "sponsor_payment", "sponsor"].includes(method)) {
    return t("payment.methods.sponsor", "AfriMed™ sponsor payment");
  }
  return method
    ? method.replace(/[-_]+/g, " ")
    : t("payment.methods.secure", "AfriMed™ secure payment");
}

export function reversalPresentation(row = {}, t = (_, fallback) => fallback) {
  const state = cleanText(row?.state || row?.reversal?.state).toLowerCase();
  if (state === "refunded") return {
    state,
    label: t("payment.reversal.refunded", "Payment refunded"),
    destination: t("payment.reversal.destinationOriginal", "Refunded to the original payment method"),
  };
  if (["initiating", "pending", "required"].includes(state)) return {
    state,
    label: t("payment.reversal.processing", "Refund processing"),
    destination: t("payment.reversal.destinationPending", "Refunds return to the original payment method"),
  };
  if (["failed", "manual_attention"].includes(state)) return {
    state,
    label: t("payment.reversal.attention", "Refund needs attention"),
    destination: t("payment.reversal.destinationUnconfirmed", "No return of funds has been confirmed yet"),
  };
  return { state: "", label: "", destination: "" };
}

export function paymentReversalNotificationId(notification = {}) {
  const type = cleanText(notification?.type);
  if (!type.startsWith("payment_reversal:")) return "";
  const match = type.match(UUID_RE);
  return match?.[0] || "";
}

export async function loadPaymentReversalNotificationContext(client, notification = {}, t) {
  const reversalId = paymentReversalNotificationId(notification);
  if (!reversalId) return null;

  const { data: reversal, error } = await client
    .from("payment_reversals")
    .select("id,appointment_id,payment_id,state,provider,provider_reversal_reference,provider_status,amount_minor,currency,requested_at,initiated_at,verified_at,metadata")
    .eq("id", reversalId)
    .maybeSingle();
  if (error || !reversal?.appointment_id) return null;

  const { data: payment } = await client
    .from("payments")
    .select("id,appointment_id,amount,amount_cents,currency,status,method,meta,created_at,updated_at")
    .eq("id", reversal.payment_id)
    .maybeSingle();

  return {
    reversal,
    payment: payment || null,
    appointmentId: String(reversal.appointment_id),
    paymentMethod: paymentMethodLabel(payment || { method: reversal?.metadata?.original_payment_method }, t),
    amount: Number(reversal.amount_minor || payment?.amount_cents || payment?.amount || 0) / 100,
    currency: String(reversal.currency || payment?.currency || "ZAR").toUpperCase(),
  };
}
